import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "@/lib/api";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Home, Loader2, ShieldAlert, CheckCircle2, XCircle, AlertTriangle, Send, Search } from "lucide-react";

/**
 * Hidden admin-only email-dispatch telemetry.
 * Route: `/admin/email-log` — no nav link anywhere. Gated on role=Admin
 * (backend enforces the same gate on `/api/admin/email-log`).
 */
const RESULT_META = {
  sent:         { label: "Sent",          color: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: CheckCircle2 },
  no_email:     { label: "No Email",      color: "text-amber-800 bg-amber-50 border-amber-200",       Icon: AlertTriangle },
  no_emp_no:    { label: "No Emp No",     color: "text-amber-800 bg-amber-50 border-amber-200",       Icon: AlertTriangle },
  graph_error:  { label: "Graph Error",   color: "text-rose-800 bg-rose-50 border-rose-200",           Icon: XCircle },
  render_error: { label: "Render Error",  color: "text-rose-800 bg-rose-50 border-rose-200",           Icon: XCircle },
};

export default function AdminEmailLog() {
  const nav = useNavigate();
  const me = auth.user();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ rows: [], counters: {}, stages: [] });
  const [days, setDays] = useState(7);
  const [filter, setFilter] = useState("");
  const [stage, setStage] = useState("");
  const [vocNo, setVocNo] = useState("");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!me) { nav("/login?redirect=/admin/email-log"); return; }
    if ((me.role || "").toLowerCase() !== "admin") { setError("Admin access only."); setLoading(false); return; }
    load();
  }, [days, filter, stage, vocNo, search]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ since_days: days, limit: 500 });
      if (filter) params.set("result", filter);
      if (stage) params.set("stage", stage);
      if (vocNo) params.set("voc_no", vocNo);
      if (search.trim()) params.set("q", search.trim());
      const { data } = await api.get(`/admin/email-log?${params.toString()}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load email log");
    } finally {
      setLoading(false);
    }
  };

  const resend = async (row) => {
    setBusyId(row.log_id);
    try {
      const { data: d } = await api.post("/admin/email-log/resend",
        { log_id: row.log_id });
      if (d.ok) {
        toast.success(`Resend fired for PR-${row.voc_no}`);
        // Give the async dispatcher a beat to append its follow-up log line
        setTimeout(load, 1500);
      } else {
        toast.error("Resend failed — check server logs");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => data.rows || [], [data]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-md bg-white p-8 rounded-xl border border-slate-200 flex flex-col items-center text-center">
          <ShieldAlert className="w-14 h-14 text-amber-500 mb-4" />
          <h1 className="text-lg font-semibold text-slate-900">{error}</h1>
          <Button onClick={() => nav("/home")} className="mt-6" data-testid="email-log-home-btn">Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 sm:p-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-1 mb-4">
          <Button variant="ghost" size="sm" onClick={() => nav("/sap")}
                  data-testid="email-log-back-sap">
            <ArrowLeft size={16} className="mr-1" /> SAP Workspace
          </Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/home")}
                  data-testid="email-log-back-home">
            <Home size={16} className="mr-1" /> Home
          </Button>
        </div>
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">ADMIN · TELEMETRY</div>
            <h1 className="text-2xl font-semibold text-slate-900 mt-1">Email Dispatch Log</h1>
          </div>
          <div className="flex gap-2 items-center">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="text-sm border rounded-md px-2 py-1.5 bg-white"
              data-testid="email-log-days-select"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="email-log-refresh-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : "Refresh"}
            </Button>
          </div>
        </div>

        {/* Counters summary */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {Object.entries(RESULT_META).map(([k, meta]) => {
            const n = data.counters?.[k] || 0;
            const active = filter === k;
            return (
              <button
                key={k}
                onClick={() => setFilter(active ? "" : k)}
                className={`text-left px-3 py-2 rounded-md border transition ${
                  active ? meta.color + " ring-2 ring-offset-1 ring-slate-400" : "bg-white border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`email-log-filter-${k}`}
              >
                <div className={`flex items-center gap-2 text-xs font-medium uppercase tracking-wider ${active ? "" : "text-slate-500"}`}>
                  <meta.Icon size={12} /> {meta.label}
                </div>
                <div className="text-2xl font-semibold text-slate-900 mt-1 font-variant-numeric-tabular">{n}</div>
              </button>
            );
          })}
        </div>

        {/* Secondary filters — voc/stage/search */}
        <div className="bg-white rounded-md border border-slate-200 p-3 mb-4 flex flex-wrap items-center gap-2"
             data-testid="email-log-filters-secondary">
          <Input type="number" placeholder="PR #" value={vocNo}
                 onChange={(e) => setVocNo(e.target.value)}
                 className="h-8 w-24 text-sm font-mono"
                 data-testid="email-log-voc-input" />
          <select value={stage} onChange={(e) => setStage(e.target.value)}
                  className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white"
                  data-testid="email-log-stage-select">
            <option value="">All stages</option>
            {(data.stages || []).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <div className="relative flex-1 min-w-[220px]">
            <Search size={12} className="absolute top-2.5 left-2 text-slate-400" />
            <Input placeholder="Search name / email / error detail"
                   value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   className="h-8 text-sm pl-7"
                   data-testid="email-log-search-input" />
          </div>
          {(stage || vocNo || search) && (
            <Button size="sm" variant="ghost"
                    onClick={() => { setStage(""); setVocNo(""); setSearch(""); }}
                    data-testid="email-log-clear-btn">
              Clear
            </Button>
          )}
        </div>

        {/* Log table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-3 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">VOC</th>
                <th className="px-3 py-2 font-medium">Stage</th>
                <th className="px-3 py-2 font-medium">Recipient</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Result</th>
                <th className="px-3 py-2 font-medium">Detail</th>
                <th className="px-3 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && filtered.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-500">
                  <Loader2 size={16} className="animate-spin inline mr-2" />Loading…
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-500">No dispatches in window.</td></tr>
              ) : filtered.map((r) => {
                const meta = RESULT_META[r.result] || { label: r.result, color: "text-slate-700 bg-slate-100 border-slate-200", Icon: AlertTriangle };
                const canResend = r.voc_no && r.log_id && ["graph_error","render_error","no_email"].includes(r.result);
                return (
                  <tr key={r.log_id || `${r.ts}-${r.voc_no}`} className="border-t border-slate-100 hover:bg-slate-50"
                      data-testid={`email-log-row-${r.log_id || r.voc_no}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600 font-variant-numeric-tabular">{new Date(r.ts).toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono text-slate-900">
                      {r.voc_no ? (
                        <button className="text-cyan-700 hover:underline"
                                onClick={() => setVocNo(String(r.voc_no))}
                                data-testid={`email-log-voc-${r.log_id}`}>
                          {r.voc_no}
                        </button>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 uppercase text-[11px] tracking-wider text-slate-600">{r.stage}</td>
                    <td className="px-3 py-2">
                      <div className="text-slate-900">{r.recipient_name || "—"}</div>
                      <div className="text-[10px] font-mono text-slate-500">{r.recipient_emp_no || ""}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700 font-mono text-[12px]">{r.recipient_email || <span className="italic text-slate-400">none</span>}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${meta.color}`}>
                        <meta.Icon size={11} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-[12px] max-w-[280px] truncate" title={r.detail}>{r.detail || ""}</td>
                    <td className="px-3 py-2 text-right">
                      {canResend && (
                        <Button size="sm" variant="ghost" onClick={() => resend(r)}
                                disabled={busyId === r.log_id}
                                className="h-6 text-[11px] text-cyan-700 hover:bg-cyan-50 px-2"
                                data-testid={`email-log-resend-${r.log_id}`}>
                          {busyId === r.log_id ? <Loader2 size={11} className="animate-spin" /> : <><Send size={11} className="mr-1" /> Resend</>}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-[11px] text-slate-500 mt-3">
          Showing {filtered.length} of {data.total_returned || 0} · Window: last {data.since_days} day(s)
          {filter ? <> · Filter: <b>{RESULT_META[filter]?.label}</b> — click again to clear</> : null}
        </div>
      </div>
    </div>
  );
}
